// ===== Interfaces ======

import { ITranscriptionWord } from '../interfaces';

const WORD_PATTERN = /[A-Za-z0-9]+(?:['’\u2010\u2011-][A-Za-z0-9]+)*/g;

interface ITranscriptAlternativeLike {
    transcript?: string,
    words?: ITranscriptionWord[],
    [key: string]: unknown,
}

interface ITranscriptPhraseLike {
    alternatives?: ITranscriptAlternativeLike[],
    [key: string]: unknown,
}

interface ITranscriptPayloadLike {
    data?: ITranscriptPhraseLike[],
    [key: string]: unknown,
}

/**
 * Normalizes transcript and post text words so prefix trimming can compare
 * word counts without punctuation or casing mismatches.
 */
function normalizeTranscriptWord(value: string): string {
    let normalizedWord = value
        .trim()
        .toLowerCase()
        .replace(/[’]/g, '\'')
        .replace(/[\u2010\u2011]/g, '-');

    while (normalizedWord && !/[a-z0-9]/.test(normalizedWord[0])) {
        normalizedWord = normalizedWord.slice(1);
    }
    while (normalizedWord && !/[a-z0-9]/.test(normalizedWord[normalizedWord.length - 1])) {
        normalizedWord = normalizedWord.slice(0, -1);
    }
    while (normalizedWord.startsWith('\'') || normalizedWord.startsWith('-')) {
        normalizedWord = normalizedWord.slice(1);
    }
    while (normalizedWord.endsWith('\'') || normalizedWord.endsWith('-')) {
        normalizedWord = normalizedWord.slice(0, -1);
    }

    return normalizedWord;
}

function countNormalizedWordsInText(value: string): number {
    return Array.from(value.matchAll(WORD_PATTERN)).reduce((count, match) => {
        const normalizedWord = normalizeTranscriptWord(match[0] || '');
        return normalizedWord ? count + 1 : count;
    }, 0);
}

function countTranscriptPayloadWords(transcript: ITranscriptPayloadLike): number {
    const transcriptData = Array.isArray(transcript.data)
        ? transcript.data
        : [];

    return transcriptData.reduce((count, phrase) => {
        const firstAlternative = Array.isArray(phrase.alternatives)
            ? phrase.alternatives[0]
            : null;

        if (
            firstAlternative
            && Array.isArray(firstAlternative.words)
            && firstAlternative.words.length > 0
        ) {
            return count + firstAlternative.words.reduce((wordCount, word) => (
                normalizeTranscriptWord(word.word || '')
                    ? wordCount + 1
                    : wordCount
            ), 0);
        }

        if (typeof firstAlternative?.transcript !== 'string') {
            return count;
        }

        return count + countNormalizedWordsInText(firstAlternative.transcript);
    }, 0);
}

function trimTranscriptTextToWordCount(value: string, wordCount: number): string {
    if (wordCount <= 0) {
        return '';
    }

    const matches = Array.from(value.matchAll(WORD_PATTERN)).filter((match) => (
        normalizeTranscriptWord(match[0] || '').length > 0
    ));
    if (matches.length <= wordCount) {
        return value.trim();
    }

    const finalMatch = matches[wordCount - 1];
    const finalWord = finalMatch[0] || '';
    const finalWordStart = finalMatch.index ?? 0;
    return value.slice(0, finalWordStart + finalWord.length).trim();
}

/**
 * Preview posts only expose a prefix of the post text. When an audio note is
 * marked as the post dictation, trim its transcript payload to that same word
 * prefix so readers cannot reveal full transcript text beyond the cutoff.
 */
function trimAudioNoteTranscriptToSourceText<T extends ITranscriptPayloadLike>(
    transcript: T | null | undefined,
    sourceText: string | null | undefined,
): T | null {
    if (!transcript || !Array.isArray(transcript.data)) {
        return transcript || null;
    }

    const normalizedSourceText = String(sourceText || '').trim();
    if (!normalizedSourceText) {
        return transcript;
    }

    const sourceWordCount = countNormalizedWordsInText(normalizedSourceText);
    if (sourceWordCount <= 0) {
        return transcript;
    }

    const transcriptWordCount = countTranscriptPayloadWords(transcript);
    if (sourceWordCount >= transcriptWordCount) {
        return transcript;
    }

    let remainingWordCount = sourceWordCount;
    const trimmedData = transcript.data.reduce<ITranscriptPhraseLike[]>((phrases, phrase) => {
        if (remainingWordCount <= 0) {
            return phrases;
        }

        const alternatives = Array.isArray(phrase.alternatives)
            ? phrase.alternatives
            : [];
        const firstAlternative = alternatives[0];
        if (!firstAlternative) {
            return phrases;
        }

        if (Array.isArray(firstAlternative.words) && firstAlternative.words.length > 0) {
            const retainedWords: ITranscriptionWord[] = [];
            firstAlternative.words.forEach((word) => {
                if (remainingWordCount <= 0) {
                    return;
                }

                if (!normalizeTranscriptWord(word.word || '')) {
                    return;
                }

                retainedWords.push(word);
                remainingWordCount -= 1;
            });

            if (retainedWords.length === 0) {
                return phrases;
            }

            phrases.push({
                ...phrase,
                alternatives: [
                    {
                        ...firstAlternative,
                        words: retainedWords,
                        transcript: retainedWords.map((word) => word.word).join(' ').trim(),
                    },
                    ...alternatives.slice(1),
                ],
            });
            return phrases;
        }

        if (typeof firstAlternative.transcript !== 'string') {
            return phrases;
        }

        const phraseWordCount = countNormalizedWordsInText(firstAlternative.transcript);
        if (phraseWordCount <= 0) {
            return phrases;
        }

        const retainedWordCount = Math.min(remainingWordCount, phraseWordCount);
        const trimmedTranscriptText = trimTranscriptTextToWordCount(
            firstAlternative.transcript,
            retainedWordCount,
        );
        if (!trimmedTranscriptText) {
            return phrases;
        }

        remainingWordCount -= retainedWordCount;
        phrases.push({
            ...phrase,
            alternatives: [
                {
                    ...firstAlternative,
                    transcript: trimmedTranscriptText,
                },
                ...alternatives.slice(1),
            ],
        });
        return phrases;
    }, []);

    return {
        ...transcript,
        data: trimmedData,
    };
}

export default trimAudioNoteTranscriptToSourceText;
